import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

const documentNames: Record<string, string> = {
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  cookie: "Cookie Policy",
  referral_terms: "Referral Terms",
};

const documentUrls: Record<string, string> = {
  terms: "/legal/terms",
  privacy: "/legal/privacy",
  cookie: "/legal/cookies",
  referral_terms: "/legal/referral-terms",
};

interface PolicyChangeNotificationProps {
  userId: string;
}

export function PolicyChangeNotification({ userId }: PolicyChangeNotificationProps) {
  const navigate = useNavigate();

  useEffect(() => {
    checkForPolicyChanges();
  }, [userId]);

  const checkForPolicyChanges = async () => {
    try {
      // Get latest versions
      const { data: latestVersions } = await supabase
        .from("legal_document_versions")
        .select("*")
        .order("published_at", { ascending: false });

      if (!latestVersions) return;

      // Get user's acceptances
      const { data: acceptances } = await supabase
        .from("legal_acceptances")
        .select("document_type, version, accepted_at")
        .eq("user_id", userId);

      const acceptanceMap = new Map(
        (acceptances || []).map((a) => [a.document_type, a])
      );

      // Find documents updated in last 30 days that user hasn't seen notification for
      const recentUpdates = latestVersions.filter((version) => {
        const publishedDate = new Date(version.published_at);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        if (publishedDate < thirtyDaysAgo) return false;

        const userAcceptance = acceptanceMap.get(version.document_type);
        
        // Don't notify if requires re-acceptance (modal will handle that)
        if (version.requires_reacceptance) return false;

        // Don't notify if user has already accepted this version
        if (userAcceptance && userAcceptance.version === version.version) {
          return false;
        }

        // Check if we've already shown this notification
        const notificationKey = `policy_notification_${version.document_type}_${version.version}`;
        if (localStorage.getItem(notificationKey)) {
          return false;
        }

        return true;
      });

      // Show toast for each recent update
      recentUpdates.forEach((update) => {
        const docName = documentNames[update.document_type] || update.document_type;
        const url = documentUrls[update.document_type];

        toast(
          `Our ${docName} has been updated`,
          {
            description: update.change_summary || "Review the changes to stay informed.",
            action: url ? {
              label: "Review Changes",
              onClick: () => navigate(url)
            } : undefined,
            icon: <FileText className="h-4 w-4" />,
          }
        );

        // Mark as notified
        const notificationKey = `policy_notification_${update.document_type}_${update.version}`;
        localStorage.setItem(notificationKey, "true");
      });
    } catch (error) {
      console.error("Error checking policy changes:", error);
    }
  };

  return null;
}
